import React, { useState, useEffect } from 'react';
import { getRentalListings } from '../../services/propertyService';
import { getRentalAccount } from '../../services/rentalAccountService';
import ListingsModal from '../ListingsModal';
import RentalAgreementModal from '../RentalAgreementModal';
import BondModal from '../BondModal';

const MultiRentalListingSection = ({
  property,
  refreshTrigger,
  selectedListingId: externalSelectedListingId,
  initialListingId = null,
  onAgreementSelect,
  onAccountLoad,
  onListingSelect
}) => {
  const [rentalListings, setRentalListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listingBalances, setListingBalances] = useState({}); // Store balances by listing ID

  // Modal states
  const [showListingsModal, setShowListingsModal] = useState(false);
  const [showRentalAgreementModal, setShowRentalAgreementModal] = useState(false);
  const [showBondModal, setShowBondModal] = useState(false);
  const [bondAgreement, setBondAgreement] = useState(null);
  const [bondOccupant, setBondOccupant] = useState(null);

  // Fetch listings when property changes
  useEffect(() => {
    const fetchRentalListings = async () => {
      if (!property?.id) return;

      try {
        setLoading(true);
        setError(null);

        const agencyId = sessionStorage.getItem('agencyId');
        if (!agencyId) {
          throw new Error('No agency ID found in session storage');
        }

        console.log('[MultiRentalListingSection] Fetching rental listings for property:', property.id);
        const listingsData = await getRentalListings(agencyId, property.id);

        const safeListings = Array.isArray(listingsData) ? listingsData : [];
        setRentalListings(safeListings);

        // Fetch balances for all listings with active tenancies
        const balances = {};
        await Promise.all(
          safeListings.map(async (listing) => {
            if (listing.current_tenancy?.tenancy_ledger_id) {
              try {
                const account = await getRentalAccount(listing.current_tenancy.tenancy_ledger_id);
                if (account?.balance) {
                  balances[listing.id] = account.balance;
                }
              } catch (error) {
                console.error(`Error fetching balance for listing ${listing.id}:`, error);
              }
            }
          })
        );
        setListingBalances(balances);

        // Auto-select: prefer the listing requested via URL param, fall back to first
        if (safeListings.length > 0 && !selectedListingId) {
          const target = initialListingId
            ? safeListings.find(l => l.id === Number(initialListingId))
            : null;
          setSelectedListingId(target ? target.id : safeListings[0].id);
        }
      } catch (error) {
        console.error('[MultiRentalListingSection] Error fetching rental listings:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRentalListings();
  }, [property]);

  // Handle refresh trigger from parent without losing selection
  useEffect(() => {
    const refreshListings = async () => {
      if (!property?.id || refreshTrigger === 0) return;

      try {
        const agencyId = sessionStorage.getItem('agencyId');
        if (!agencyId) return;

        console.log('[MultiRentalListingSection] Refreshing listings. Current selectedListingId:', selectedListingId);
        const listingsData = await getRentalListings(agencyId, property.id);
        const safeListings = Array.isArray(listingsData) ? listingsData : [];

        setRentalListings(safeListings);

        // Fetch updated balances for all listings with active tenancies
        const balances = {};
        await Promise.all(
          safeListings.map(async (listing) => {
            if (listing.current_tenancy?.tenancy_ledger_id) {
              try {
                const account = await getRentalAccount(listing.current_tenancy.tenancy_ledger_id);
                if (account?.balance) {
                  balances[listing.id] = account.balance;
                }
              } catch (error) {
                console.error(`Error fetching balance for listing ${listing.id}:`, error);
              }
            }
          })
        );
        setListingBalances(balances);

        // Maintain the current selection if it still exists in the updated data
        if (selectedListingId) {
          const stillExists = safeListings.find(l => l.id === selectedListingId);
          if (!stillExists && safeListings.length > 0) {
            // If current selection doesn't exist anymore, select first
            setSelectedListingId(safeListings[0].id);
          }
        }
      } catch (error) {
        console.error('[MultiRentalListingSection] Error refreshing listings:', error);
      }
    };

    refreshListings();
  }, [refreshTrigger]);


  // Handle listing selection change
  useEffect(() => {
    console.log('[MultiRentalListingSection] Listing selection changed. selectedListingId:', selectedListingId);

    if (selectedListingId && rentalListings) {
      const listing = rentalListings.find(l => l.id === selectedListingId);
      console.log('[MultiRentalListingSection] Found listing:', listing?.room?.number || listing?.title);

      if (listing) {
        onListingSelect(listing);

        // Handle agreement selection
        if (listing.current_tenancy?.rental_agreement) {
          onAgreementSelect(listing.current_tenancy.rental_agreement);

          // Load rental account if tenancy exists
          if (listing.current_tenancy.tenancy_ledger_id) {
            getRentalAccount(listing.current_tenancy.tenancy_ledger_id)
              .then(account => onAccountLoad(account))
              .catch(error => {
                console.error('Error fetching rental account:', error);
                onAccountLoad(null);
              });
          }
        } else {
          onAgreementSelect(null);
          onAccountLoad(null);
        }
      }
    }
  }, [selectedListingId, rentalListings]); // Remove callback functions from dependencies

  // Event handlers
  const handleSaveListings = (listings) => {
    setRentalListings(listings);
    setShowListingsModal(false);
  };

  const handleSaveAgreement = async () => {
    // Refresh listings data after agreement save
    try {
      console.log('[MultiRentalListingSection] Refreshing listings after agreement save. Current selectedListingId:', selectedListingId);

      const agencyId = sessionStorage.getItem('agencyId');
      if (!agencyId) return;

      const listingsData = await getRentalListings(agencyId, property.id);
      const safeListings = Array.isArray(listingsData) ? listingsData : [];

      console.log('[MultiRentalListingSection] Fetched listings:', safeListings.length, 'Maintaining selectedListingId:', selectedListingId);

      setRentalListings(safeListings);

      // Fetch updated balances for all listings with active tenancies
      const balances = {};
      await Promise.all(
        safeListings.map(async (listing) => {
          if (listing.current_tenancy?.tenancy_ledger_id) {
            try {
              const account = await getRentalAccount(listing.current_tenancy.tenancy_ledger_id);
              if (account?.balance) {
                balances[listing.id] = account.balance;
              }
            } catch (error) {
              console.error(`Error fetching balance for listing ${listing.id}:`, error);
            }
          }
        })
      );
      setListingBalances(balances);

      // Note: The useEffect will handle updating callbacks when rentalListings changes
      // This ensures the selectedListingId is maintained

      setShowRentalAgreementModal(false);
    } catch (error) {
      console.error('Error refreshing listings:', error);
      setShowRentalAgreementModal(false);
    }
  };

  const handleOpenBondModal = (tenancy, occupant) => {
    setBondAgreement(tenancy);
    setBondOccupant(occupant);
    setShowBondModal(true);
  };

  const selectedListing = rentalListings.find(listing => listing.id === selectedListingId);

  // Loading state
  if (loading) {
    return (
      <div className="card shadow-sm mb-4">
        <div className="card-body text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading rental listings...</span>
          </div>
          <p className="mt-2 text-muted">Loading room listings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="alert alert-warning">
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-building me-2"></i>
              Room Listings
            </h5>
            <button
              className="btn btn-outline-primary"
              onClick={() => setShowListingsModal(true)}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Manage Listings
            </button>
          </div>
        </div>

        <div className="card-body">
          {rentalListings.length > 0 ? (
            <>
              {/* Room selection dropdown */}
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-house-door me-2"></i>
                    Select Room
                  </label>
                  <select
                    className="form-select form-select-lg"
                    value={selectedListingId || ''}
                    onChange={(e) => {
                      const newId = Number(e.target.value);
                      console.log('[MultiRentalListingSection] User changed listing selection to:', newId);
                      setSelectedListingId(newId);
                    }}
                  >
                    {rentalListings.map(listing => {
                      const roomName = listing.room ? `${listing.room.number}` : listing.title;
                      const balance = listingBalances[listing.id];

                      if (balance) {
                        const balanceDisplay = balance.formatted || `${balance.currency || ''} ${(balance.amount / 100).toFixed(2)}`;
                        return (
                          <option key={listing.id} value={listing.id}>
                            {roomName} - Balance: {balanceDisplay}
                          </option>
                        );
                      }

                      return (
                        <option key={listing.id} value={listing.id}>
                          {roomName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-house-door text-muted fs-1"></i>
              <h5 className="text-muted mt-2">No Room Listings Available</h5>
              <p className="text-muted">Click "Manage Listings" above to add room listings to this property.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ListingsModal
        show={showListingsModal}
        onClose={() => setShowListingsModal(false)}
        property={property}
        rentalListings={rentalListings}
        onSave={handleSaveListings}
      />

      <RentalAgreementModal
        show={showRentalAgreementModal}
        onClose={() => setShowRentalAgreementModal(false)}
        selectedListing={selectedListing}
        onOpenBondModal={handleOpenBondModal}
        onSave={handleSaveAgreement}
      />

      <BondModal
        show={showBondModal}
        onClose={() => setShowBondModal(false)}
        tenancy={bondAgreement}
        selectedOccupant={bondOccupant}
        onSave={(tenancy, occupant) => {
          setBondAgreement(tenancy);
          setBondOccupant(occupant);
          setShowBondModal(false);
        }}
        onDataUpdate={handleSaveAgreement}
      />
    </>
  );
};

export default MultiRentalListingSection;
